/**
 * §16.1 wizard frame: 8-step stepper, draft autosave with conflict handling, per-step validation summary that
 * links each issue to its exact field (client preview via `validateLotteryConfig`; the server is authoritative),
 * progress and a "preview as participant" link. Steps render through nested routes and read the config slice
 * from react-hook-form context (`useFormContext<DraftConfigInput>()`).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import type { LotteryAdminDto } from '@windrop/contracts';
import { Button, Skeleton, useIntl } from '@windrop/ui';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Link, NavLink, Outlet, useNavigate, useOutletContext, useParams } from 'react-router';
import { ErrorState } from '../../../components/EmptyState';
import { FormErrorSummary } from '../../../components/FormField';
import { PageHeader } from '../../../components/PageHeader';
import { StatusBadge } from '../../../components/StatusBadge';
import { lotteriesApi } from '../../../lib/api/admin';
import { isApiError } from '../../../lib/api/client';
import { readRuntimeConfig } from '../../../lib/runtime-config';
import {
  DraftConfigSchema,
  WIZARD_STEPS,
  newDraftConfig,
  previewIssues,
  stepForPath,
  stepIndex,
  toDraftInput,
  type DraftConfigInput,
  type PreviewIssue,
  type WizardStepId,
} from './steps';
import { useDraftAutosave, type DraftAutosave } from './useDraftAutosave';

export interface WizardContext {
  lottery: LotteryAdminDto | null;
  lotteryId: string | null;
  issues: PreviewIssue[];
  autosave: DraftAutosave;
  changeNote: string;
  setChangeNote: (v: string) => void;
  participantRemedy: string;
  setParticipantRemedy: (v: string) => void;
  goToStep: (step: WizardStepId) => void;
}

export const useWizard = (): WizardContext => useOutletContext<WizardContext>();

const stepPath = (lotteryId: string | null, step: WizardStepId): string =>
  lotteryId ? `/lotteries/${lotteryId}/edit/${step}` : `/lotteries/new/${step}`;

export function WizardFrame() {
  const { id, step: stepParam } = useParams();
  const lotteryId = id ?? null;
  const query = useQuery({
    queryKey: ['lottery', lotteryId],
    queryFn: () => lotteriesApi.get(lotteryId ?? ''),
    enabled: Boolean(lotteryId),
  });

  if (lotteryId && query.isPending) return <Skeleton shape="block" height={320} label="Loading draft" />;
  if (lotteryId && (query.isError || !query.data)) {
    return (
      <ErrorState
        title="Draft could not be loaded"
        body="It may belong to another tenant or have been removed."
        reference={isApiError(query.error) ? query.error.supportReference : undefined}
        action={<Button href="/lotteries">Back to lotteries</Button>}
      />
    );
  }
  return (
    <WizardForm
      key={lotteryId ?? 'new'}
      lottery={query.data ?? null}
      lotteryId={lotteryId}
      step={(stepParam as WizardStepId | undefined) ?? 'template'}
    />
  );
}

function WizardForm({
  lottery,
  lotteryId: initialId,
  step,
}: {
  lottery: LotteryAdminDto | null;
  lotteryId: string | null;
  step: WizardStepId;
}) {
  const navigate = useNavigate();
  const intl = useIntl();
  const { webOrigin } = readRuntimeConfig();
  const [lotteryId, setLotteryId] = useState(initialId);
  const [changeNote, setChangeNote] = useState('');
  const [participantRemedy, setParticipantRemedy] = useState('');

  const defaults = useMemo<DraftConfigInput>(
    () => (lottery ? toDraftInput(lottery.config) : (newDraftConfig() as DraftConfigInput)),
    [lottery],
  );
  const form = useForm<DraftConfigInput>({
    defaultValues: defaults,
    resolver: zodResolver(DraftConfigSchema) as never,
    mode: 'onBlur',
  });
  const values = useWatch({ control: form.control });

  const issues = useMemo(
    () => previewIssues(values, lottery?.configVersion ?? 1),
    [values, lottery?.configVersion],
  );
  const serverIssues: PreviewIssue[] = useMemo(
    () =>
      (lottery?.validation.issues ?? []).map((i) => ({
        ...i,
        path: i.path.replace(/\[(\d+)\]/g, '.$1'),
        step: stepForPath(i.path),
      })),
    [lottery],
  );

  const onCreated = useCallback(
    (created: LotteryAdminDto) => {
      setLotteryId(created.lotteryId);
      navigate(`/lotteries/${created.lotteryId}/edit/${step}`, { replace: true });
    },
    [navigate, step],
  );

  const autosave = useDraftAutosave({
    form,
    lotteryId,
    initialVersion: lottery?.configVersion ?? null,
    hasEntries: lottery?.hasEntries ?? false,
    changeNote,
    participantRemedy,
    onCreated,
    enabled: !lottery || ['DRAFT', 'REVIEW', 'APPROVED'].includes(lottery.status),
  });

  const current = stepIndex(step) === -1 ? 0 : stepIndex(step);
  const stepDef = WIZARD_STEPS[current] ?? WIZARD_STEPS[0]!;
  const goToStep = useCallback((s: WizardStepId) => navigate(stepPath(lotteryId, s)), [navigate, lotteryId]);
  const stepIssues = issues.filter((i) => i.step === stepDef.id);
  const countByStep = (s: WizardStepId) =>
    issues.filter((i) => i.step === s && i.severity === 'error').length;
  const slug = (values.identity?.slug as string | undefined) ?? '';
  const title = (values.presentation?.content as Record<string, { title?: string }> | undefined)?.[
    (values.identity?.locales as string[] | undefined)?.[0] ?? 'en'
  ]?.title;

  const context: WizardContext = {
    lottery,
    lotteryId,
    issues,
    autosave,
    changeNote,
    setChangeNote,
    participantRemedy,
    setParticipantRemedy,
    goToStep,
  };
  const previewHref = slug ? `${webOrigin}/l/${slug}?preview=1` : undefined;

  return (
    <FormProvider {...form}>
      <PageHeader
        breadcrumb={[
          { label: 'Lotteries', to: '/lotteries' },
          { label: lottery ? title || lottery.slug : 'New lottery' },
          { label: stepDef.label },
        ]}
        title={lottery ? title || lottery.slug : 'Create a lottery'}
        meta={lottery ? <StatusBadge kind="LotteryStatus" value={lottery.status} /> : null}
        description={stepDef.description}
        actions={
          <>
            <Button
              variant="secondary"
              href={previewHref}
              target="_blank"
              rel="noreferrer"
              disabled={!previewHref}
              disabledReason={!previewHref ? 'Set a slug first.' : undefined}
            >
              Preview as participant
            </Button>
            <Button
              variant="secondary"
              onClick={() => void autosave.saveNow()}
              loading={autosave.status.kind === 'saving'}
            >
              Save draft
            </Button>
          </>
        }
      />
      <div className="wdc-wizard">
        <nav aria-label="Wizard steps">
          <ol className="wdc-stepper">
            {WIZARD_STEPS.map((s, i) => {
              const n = countByStep(s.id);
              return (
                <li key={s.id}>
                  <NavLink
                    to={stepPath(lotteryId, s.id)}
                    aria-current={s.id === stepDef.id ? 'step' : undefined}
                    data-complete={n === 0 && i < current ? 'true' : undefined}
                    data-testid={`step-${s.id}`}
                  >
                    <span>{s.label}</span>
                    {n > 0 ? (
                      <span
                        className="wdc-stepper__count wdc-badge"
                        data-tone="error"
                        data-size="sm"
                        aria-label={`${n} issue${n === 1 ? '' : 's'}`}
                      >
                        {n}
                      </span>
                    ) : null}
                  </NavLink>
                </li>
              );
            })}
          </ol>
          <p className="wd-muted" style={{ padding: '0 var(--wd-space-3)', fontSize: 13 }}>
            Step {current + 1} of {WIZARD_STEPS.length} ·{' '}
            {issues.filter((i) => i.severity === 'error').length} blocking issue
            {issues.filter((i) => i.severity === 'error').length === 1 ? '' : 's'}
          </p>
        </nav>
        <div className="wdc-wizard__panel">
          <SaveStatusLine autosave={autosave} intl={intl} />
          {lottery?.hasEntries ? (
            <div className="wdc-callout" data-tone="warning">
              <p>
                <strong>Entries exist.</strong> Material changes create a new configuration version and need a
                change note and participant remedy (Review step) before they save (§5.4).
              </p>
            </div>
          ) : null}
          {stepDef.id !== 'review' ? (
            <FormErrorSummary
              issues={stepIssues}
              title={`${stepIssues.length} issue${stepIssues.length === 1 ? '' : 's'} on this step`}
              onNavigate={(path) => goToStep(stepForPath(path))}
            />
          ) : null}
          {serverIssues.length && stepDef.id !== 'review' ? (
            <details>
              <summary className="wd-muted">Server validation from last save ({serverIssues.length})</summary>
              <FormErrorSummary
                issues={serverIssues.filter((i) => i.step === stepDef.id)}
                title="Reported by the API"
                onNavigate={(path) => goToStep(stepForPath(path))}
              />
            </details>
          ) : null}
          <Outlet context={context} />
          <div className="wdc-wizard__nav">
            {current > 0 ? (
              <Link
                className="wd-btn"
                data-variant="secondary"
                to={stepPath(lotteryId, WIZARD_STEPS[current - 1]!.id)}
              >
                Back: {WIZARD_STEPS[current - 1]!.label}
              </Link>
            ) : (
              <span />
            )}
            {current < WIZARD_STEPS.length - 1 ? (
              <Link
                className="wd-btn"
                data-variant="primary"
                to={stepPath(lotteryId, WIZARD_STEPS[current + 1]!.id)}
              >
                Next: {WIZARD_STEPS[current + 1]!.label}
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

function SaveStatusLine({ autosave, intl }: { autosave: DraftAutosave; intl: ReturnType<typeof useIntl> }) {
  const s = autosave.status;
  return (
    <div className="wdc-wizard__status" role="status" data-testid="save-status">
      {s.kind === 'idle' ? <span>Draft autosaves as you edit.</span> : null}
      {s.kind === 'dirty' ? <span>Unsaved changes…</span> : null}
      {s.kind === 'saving' ? <span>Saving…</span> : null}
      {s.kind === 'saved' ? (
        <span>
          Saved v{s.version} at {intl.formatTime(s.at)}.
        </span>
      ) : null}
      {s.kind === 'invalid' ? <span className="text-warning">{s.message}</span> : null}
      {s.kind === 'error' ? <span className="text-error">{s.message}</span> : null}
      {s.kind === 'conflict' ? (
        <>
          <span className="text-error">Someone else saved a newer version. {s.message}</span>
          <Button variant="secondary" onClick={() => void autosave.reloadServerCopy()}>
            Load their version
          </Button>
          <Button variant="danger" onClick={() => void autosave.overwriteServerCopy()}>
            Overwrite with mine
          </Button>
        </>
      ) : null}
    </div>
  );
}
